import React, { useState } from 'react';
import { useAccount } from 'wagmi';
import { ArrowDownUp } from 'lucide-react';
import ConnectButton from "../components/Wallet/ConnectButton";
import MintForm from "../components/Bridge/MintForm";
import BurnForm from "../components/Bridge/BurnForm";
import BridgeStatePill from "../components/Bridge/BridgeStatePill";
import { useBridge } from "../hooks/useBridge";
import { useBridgeNetworkKey } from "../hooks/useBridgeNetworkKey";
import { useBridgeFeesQuery, useBridgeState } from "../hooks/bridgeQueries";

type BridgeMode = 'wrap' | 'unwrap';

export default function BridgePage() {
  const { address } = useAccount();
  const selectedNetwork = useBridgeNetworkKey();
  const [mode, setMode] = useState<BridgeMode>('wrap');
  const [isMinting, setIsMinting] = useState(false);
  const [isBurning, setIsBurning] = useState(false);

  const {
    mintTokens,
    burnTokens,
    createDepositAddress,
    submitWithdrawal,
    aliveNodes,
    mintDepositAddresses,
    burnHistory,
    isLoading: bridgeLoading,
  } = useBridge(selectedNetwork);

  const { data: bridgeFees } = useBridgeFeesQuery(selectedNetwork);
  const { state: bridgeState, isLoading: bridgeStateLoading } = useBridgeState(selectedNetwork);

  const handleMint = async (depositAddress: string) => {
    setIsMinting(true);
    try {
      await mintTokens(depositAddress);
    } finally {
      setIsMinting(false);
    }
  };

  const handleCreateDepositAddress = async () => {
    setIsMinting(true);
    try {
      await createDepositAddress();
    } finally {
      setIsMinting(false);
    }
  };

  const handleBurn = async (amount: string, destinationAddress: string) => {
    setIsBurning(true);
    try {
      await burnTokens(amount, destinationAddress);
    } finally {
      setIsBurning(false);
    }
  };

  const handleSubmitWithdrawal = async (burnIndex: number) => {
    setIsBurning(true);
    try {
      await submitWithdrawal(burnIndex);
    } finally {
      setIsBurning(false);
    }
  };

  const toggleMode = () => setMode((m) => (m === 'wrap' ? 'unwrap' : 'wrap'));

  return (
    <div className="max-w-xl mx-auto py-12 px-4">
      <div className="mb-4 flex justify-center">
        <BridgeStatePill state={bridgeState} isLoading={bridgeStateLoading} />
      </div>

      {bridgeState === 'closed' && (
        <div className="mb-6 rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-center text-sm text-red-200">
          The bridge is temporarily closed. Please check back soon.
        </div>
      )}

      <div className="mb-8 flex justify-center">
        <ConnectButton />
      </div>

      <div className="card">
        <h2 className="text-2xl font-bold text-dark-50 text-center mb-2">
          {mode === 'wrap' ? 'Wrap Dingocoin' : 'Unwrap wDingocoin'}
        </h2>
        <p className="text-sm text-dark-400 text-center mb-6">
          {mode === 'wrap'
            ? `Mint wDingocoin on ${selectedNetwork.toUpperCase()} from native Dingocoin deposits.`
            : 'Burn wDingocoin on-chain and receive native Dingocoin.'}
        </p>

        <div className="flex flex-col gap-0 mb-6">
          <div className="rounded-xl border-2 border-dark-600 bg-dark-800/80 p-4">
            <div className="text-xs font-medium text-dark-500 uppercase tracking-wide">From</div>
            <div className="text-lg font-semibold text-dark-100 mt-1">
              {mode === 'wrap' ? 'Dingocoin (native)' : 'wDingocoin'}
            </div>
            <div className="text-xs text-dark-500 mt-0.5">
              {mode === 'wrap' ? 'UTXO chain — send to your deposit address' : `On ${selectedNetwork.toUpperCase()}`}
            </div>
          </div>

          <div className="flex justify-center -my-3 relative z-10">
            <button
              type="button"
              onClick={toggleMode}
              className="group flex items-center gap-2 rounded-full border-2 border-primary-500/60 bg-primary-600/15 pl-3 pr-4 py-2 text-sm font-semibold text-primary-300 shadow-lg shadow-black/25 cursor-pointer transition-all hover:bg-primary-600/25 hover:border-primary-400 hover:text-primary-200 hover:scale-[1.03] active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-dark-800"
              aria-label={mode === 'wrap' ? 'Switch to unwrap' : 'Switch to wrap'}
              title="Click to swap wrap and unwrap"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-600/40 text-primary-100 ring-1 ring-primary-400/50 group-hover:bg-primary-600/55">
                <ArrowDownUp className="w-4 h-4" aria-hidden />
              </span>
              <span>Swap direction</span>
            </button>
          </div>

          <div className="rounded-xl border-2 border-dark-600 bg-dark-800/80 p-4">
            <div className="text-xs font-medium text-dark-500 uppercase tracking-wide">To</div>
            <div className="text-lg font-semibold text-dark-100 mt-1">
              {mode === 'wrap' ? 'wDingocoin' : 'Dingocoin (native)'}
            </div>
            <div className="text-xs text-dark-500 mt-0.5">
              {mode === 'wrap' ? `On ${selectedNetwork.toUpperCase()}` : 'Sent to your Dingocoin address'}
            </div>
          </div>
        </div>

        {mode === 'wrap' ? (
          <MintForm
            key="mint"
            onMint={handleMint}
            onCreateDepositAddress={handleCreateDepositAddress}
            isLoading={isMinting || bridgeLoading}
            isCreatingDeposit={isMinting}
            network={selectedNetwork}
            mintDepositAddresses={mintDepositAddresses}
            aliveNodes={aliveNodes}
            bridgeFees={bridgeFees}
            bridgeState={bridgeState}
          />
        ) : (
          <BurnForm
            key="burn"
            onBurn={handleBurn}
            onSubmitWithdrawal={handleSubmitWithdrawal}
            burnHistory={burnHistory || []}
            isLoading={isBurning}
            network={selectedNetwork}
            bridgeFees={bridgeFees}
          />
        )}
      </div>

      {!address && (
        <div className="mt-8 text-center">
          <p className="text-dark-400">
            Connect your wallet to start using the bridge
          </p>
        </div>
      )}
    </div>
  );
}